import { useState } from 'react';
import type { CheckoutSession, AttemptResult } from '@/lib/types';
import { CheckoutHeader } from '@/components/CheckoutHeader';

interface Props {
  session: CheckoutSession;
  attempt: AttemptResult;
}

export function ReferenceDisplay({ session, attempt }: Props) {
  const [copied, setCopied] = useState(false);

  const nextAction = attempt.next_action;
  const data = (nextAction.data ?? nextAction) as Record<string, unknown>;
  const transferKey = (data.transferKey ?? nextAction.transferKey) as string | undefined;
  const rail = (data.rail ?? nextAction.rail) as string | undefined;
  const expiresAt = (data.expiresAt ?? nextAction.expiresAt) as string | undefined;

  const expiresFormatted = expiresAt
    ? new Intl.DateTimeFormat('es-CO', { hour: '2-digit', minute: '2-digit' }).format(new Date(expiresAt))
    : null;

  const handleCopy = async () => {
    if (!transferKey) return;
    try {
      await navigator.clipboard.writeText(transferKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select text
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-start p-4 pt-8 pb-8">
      <div className="w-full max-w-md">
        {/* Merchant + amount header */}
        <CheckoutHeader session={session} />

        {/* Reference Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 text-sm font-bold">B</span>
            </div>
            <h2 className="text-base font-semibold text-gray-900">
              {rail === 'bre_b' ? 'Transferencia BRE-B' : 'Transferencia bancaria'}
            </h2>
          </div>

          <p className="text-sm text-gray-500 mb-4">
            Usa esta referencia para completar tu pago desde tu app bancaria.
          </p>

          {/* Transfer Key */}
          {transferKey && (
            <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4 mb-4">
              <p className="text-xs text-gray-500 mb-1 uppercase tracking-wider">Referencia de pago</p>
              <div className="flex items-center justify-between gap-3">
                <p className="text-2xl font-mono font-bold text-gray-900 tracking-wider break-all">{transferKey}</p>
                <button
                  onClick={handleCopy}
                  className="flex-shrink-0 text-xs bg-brand-500 hover:bg-brand-600 text-white px-3 py-1.5 rounded-lg transition-colors min-h-[36px]"
                >
                  {copied ? '✓ Copiado' : 'Copiar'}
                </button>
              </div>
            </div>
          )}

          {/* Instructions */}
          <ol className="text-sm text-gray-600 space-y-2 mb-4">
            <li className="flex gap-2">
              <span className="font-bold text-brand-500 w-4 flex-shrink-0">1.</span>
              Abre tu app bancaria y selecciona <strong>Transferir por BRE-B</strong>.
            </li>
            <li className="flex gap-2">
              <span className="font-bold text-brand-500 w-4 flex-shrink-0">2.</span>
              Ingresa la referencia de arriba como destino. El monto se precargará automáticamente.
            </li>
            <li className="flex gap-2">
              <span className="font-bold text-brand-500 w-4 flex-shrink-0">3.</span>
              Esta página se actualizará automáticamente al confirmar tu pago.
            </li>
          </ol>

          {/* Expiry */}
          {expiresFormatted && (
            <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Esta referencia expira a las {expiresFormatted}</span>
            </div>
          )}
        </div>

        {/* Polling indicator */}
        <div className="flex items-center justify-center gap-2 text-sm text-gray-400 py-2">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-brand-500 rounded-full animate-spin" />
          <span>Esperando confirmación de pago...</span>
        </div>

        <p className="text-center text-xs text-gray-400 mt-2">
          Puedes cerrar esta página con seguridad. Volveremos a sincronizar el estado automáticamente al regresar.
        </p>

        <p className="text-center text-xs text-gray-400 mt-2 mb-2">
          Pago procesado de forma segura por Orangepill
        </p>
      </div>
    </div>
  );
}
